import React, { useRef, useEffect, useState } from 'react';
import { useHistory, useLocation } from "react-router-dom";
import { BsPostcardHeart } from 'react-icons/bs';
import MediaSwitcher from './MediaSwitcher';
import TextToHTML from './TextToHtml';
import TagList from './TagList';
import { PiCoins } from 'react-icons/pi';
import { IoMdImages } from 'react-icons/io';
import { LuFileVideo2 } from 'react-icons/lu';
import { TbShoppingBagPlus } from "react-icons/tb";

// Referencia global al video que se está reproduciendo
let currentPlayingVideo = null;

function CardPack(props) {
    const mediaSwitcherRef = useRef(null);
    const history = useHistory();
    const location = useLocation();

    // Función para pausar el video
    const pauseVideo = () => {
        if (mediaSwitcherRef.current) {
            const videoElement = mediaSwitcherRef.current.querySelector('video');
            if (videoElement && !videoElement.paused) {
                videoElement.pause();
            }
        }
    };

    // Función para manejar el inicio de reproducción del video
    const handleVideoPlay = () => {
        if (mediaSwitcherRef.current) {
            const videoElement = mediaSwitcherRef.current.querySelector('video');
            if (videoElement) {
                if (currentPlayingVideo && currentPlayingVideo !== videoElement) {
                    currentPlayingVideo.pause();
                }
                currentPlayingVideo = videoElement;
            }
        }
    };

    const handleVideoClick = (event) => {
        event.stopPropagation();
    };

    useEffect(() => {
        const observerCallback = (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    pauseVideo();
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, {
            root: null,
            threshold: 0.1
        });

        if (mediaSwitcherRef.current) {
            observer.observe(mediaSwitcherRef.current);

            const videoElement = mediaSwitcherRef.current.querySelector('video');
            if (videoElement) {
                videoElement.addEventListener('play', handleVideoPlay);
                videoElement.addEventListener('click', handleVideoClick);
            }
        }

        return () => {
            if (mediaSwitcherRef.current) {
                observer.unobserve(mediaSwitcherRef.current);

                const videoElement = mediaSwitcherRef.current.querySelector('video');
                if (videoElement) {
                    videoElement.removeEventListener('play', handleVideoPlay);
                    videoElement.removeEventListener('click', handleVideoClick);
                }
            }
        };
    }, []);

    const handleMediaClick = (file) => {
        const mediaUrls = props.pack.files.map(file => ({
            url: file.preview_file || file.processed_file,
            file_format: file.file_type
        }));
        const videoIndex = mediaUrls.findIndex(url => url.url === (file.preview_file || file.processed_file));
        if (videoIndex !== -1) {
            mediaUrls[videoIndex].url = props.pack.files[videoIndex].processed_file || props.pack.files[videoIndex].preview_file;
        }
        // Remove duplicate thumbnails for videos
        const uniqueMediaUrls = mediaUrls.filter((url, index, self) =>
            index === self.findIndex((u) => (
                u.url === url.url
            ))
        );
        props.onMediaClick(uniqueMediaUrls);
    };

    // Lógica del modo oscuro
    const [darkMode, setDarkMode] = useState(() => {
        const savedMode = localStorage.getItem('darkMode');
        return savedMode !== null ? JSON.parse(savedMode) : false;
    });

    useEffect(() => {
        document.documentElement.classList.toggle('dark', darkMode);
        localStorage.setItem('darkMode', JSON.stringify(darkMode));
    }, [darkMode]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            localStorage.setItem('darkMode', JSON.stringify(darkMode));
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [darkMode]);

    const onAvatarClick = (userId) => {
        const origin = location.state?.background || location;
        const base = origin.pathname.split('/')[1];
        const publicBases = ['', 'packs', 'listPacks', 'feed', 'FeedPublic', 'profileModal', 'FeedByUser'];
        if (base && !publicBases.includes(base)) {
            history.push(`/${base}/profileModal/${userId}`, { background: origin });
        } else {
            history.push(`/profileModal/${userId}`, { background: origin });
        }
    };

    // Verifica si la URL actual contiene "profileModal"
    const isInProfileModal = location.pathname.includes("profileModal");

    return (
        <div className="overflow-hidden w-[350px] min-h-[400px] rounded-[24px] mx-auto flex flex-col transition-transform duration-300 ease-out hover:scale-[1.015] dark:ring-1 dark:ring-white/[0.06]"
            style={{ fontFamily: 'var(--ap-font)', background: 'var(--ap-surface)', color: 'var(--ap-label)', boxShadow: '0 1px 2px rgba(0,0,0,.06), 0 12px 32px rgba(0,0,0,.12)' }}>
            
            {/* MediaSwitcher */}
            <div className="relative flex-shrink-0">
                <MediaSwitcher 
                    files={props.pack.files.map(file => file.preview_file || file.processed_file)} 
                    className="p-0" 
                    ref={mediaSwitcherRef}
                />
                <div
                    className="absolute inset-0 bg-transparent z-10 cursor-pointer"
                    onClick={() => handleMediaClick(props.pack.files[0])}
                ></div>
            </div>
    
            {/* Contenido principal */}
            <div className="p-4 sm:p-5 flex-1 flex flex-col">
                <div className="flex-1">
                    {/* Sección Superior */}
                    <div className="flex gap-4 mb-4 h-[90px]">
                        {/* Descripción */}
                        <div className="flex-1 p-3 rounded-[12px] overflow-auto h-[80px] text-[13px] leading-5" style={{ background: 'var(--ap-fill)' }}>
                            <TextToHTML text={props.pack.description} />
                        </div>
    
                        {/* Avatar */}
                        {!isInProfileModal && (
                            <div className="flex-shrink-0">
                                <img
                                    src={props.pack.avatar_thumb_url || props.pack.model_profile_cover}
                                    alt="Profile"
                                    className="w-20 h-20 rounded-full object-cover cursor-pointer transition-transform hover:scale-105"
                                    style={{ boxShadow: '0 0 0 0.5px var(--ap-separator)' }}
                                    onClick={() => onAvatarClick(props.pack.user_id)}
                                />
                            </div>
                        )}
                    </div>
    
                    {/* Estadísticas */}
                    <div className="grid grid-cols-3 gap-2 mb-4 text-[13px] font-medium">
                        <div className="flex items-center justify-center">
                            <PiCoins className="mr-1.5 text-lg" style={{ color: 'var(--ap-tint)' }} />
                            <span>{props.pack.tokens || 'Free'}</span>
                        </div>
                        <div className="flex items-center justify-center">
                            <IoMdImages className="mr-1.5 text-lg" style={{ color: 'var(--ap-label-2)' }} />
                            <span>{props.pack.num_images}</span>
                        </div>
                        <div className="flex items-center justify-center">
                            <LuFileVideo2 className="mr-1.5 text-lg" style={{ color: 'var(--ap-label-2)' }} />
                            <span>{props.pack.num_videos}</span>
                        </div>
                    </div>
    
                    {/* Tags - Altura reducida a 60px */}
                    <div className="min-h-[60px] max-h-[80px] overflow-auto">
                        <TagList tags={props.pack.tags.split(',').map(String)} />
                    </div>
                </div>
    
                {/* Botón pegado al fondo */}
                <div className="mt-auto pt-3">
                    <button
                        onClick={() => props.onClick(props.pack)}
                        className="ap-btn ap-btn-filled w-full">
                        <TbShoppingBagPlus className="text-xl" />
                        <span>Buy Pack</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CardPack;
